<!--
  One button, three weights. Every interactive surface in the app uses it, so
  "the save button looks different over here" cannot happen.
-->
<script lang="ts">
  import type { Snippet } from 'svelte';
  import Icon, { type IconName } from './Icon.svelte';
  import Spinner from './Spinner.svelte';

  interface Props {
    children: Snippet;
    variant?: 'primary' | 'secondary' | 'hero' | 'ghost';
    size?: 'md' | 'lg';
    icon?: IconName | undefined;
    /** Renders an <a> instead of a <button>; the router picks it up as a link. */
    href?: string | undefined;
    disabled?: boolean;
    loading?: boolean;
    /** Stretch to the container width — the phone default for a primary action. */
    block?: boolean;
    title?: string;
    type?: 'button' | 'submit';
    onclick?: (event: MouseEvent) => void;
  }

  let {
    children,
    variant = 'secondary',
    size = 'md',
    icon,
    href,
    disabled = false,
    loading = false,
    block = false,
    title,
    type = 'button',
    onclick
  }: Props = $props();

  const classes = $derived(`btn ${variant} ${size}${block ? ' block' : ''}`);
</script>

{#if href && !disabled}
  <a class={classes} {href} {title} {onclick}>
    {#if loading}
      <Spinner size={size === 'lg' ? 17 : 15} />
    {:else if icon}
      <Icon name={icon} size={size === 'lg' ? 19 : 17} />
    {/if}
    <span>{@render children()}</span>
  </a>
{:else}
  <button class={classes} {type} {title} disabled={disabled || loading} {onclick}>
    {#if loading}
      <Spinner size={size === 'lg' ? 17 : 15} />
    {:else if icon}
      <Icon name={icon} size={size === 'lg' ? 19 : 17} />
    {/if}
    <span>{@render children()}</span>
  </button>
{/if}

<style>
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 38px;
    padding-inline: 15px;
    border-radius: 99px;
    border: 1px solid transparent;
    font-size: 0.86rem;
    font-weight: 600;
    letter-spacing: -0.005em;
    white-space: nowrap;
    transition:
      background-color 150ms var(--ease),
      border-color 150ms var(--ease),
      color 150ms var(--ease),
      opacity 150ms var(--ease);
  }
  .lg {
    height: 46px;
    padding-inline: 20px;
    font-size: 0.94rem;
  }

  /**
   * THE ORDINARY BUTTON IS 38 PX, WHICH IS FINE FOR A MOUSE AND SHORT FOR A THUMB.
   *
   * Measured at 390x844: "Change handle" 146.8x38, "Back to home" 160.7x38, and
   * every other `md` button on Profile, Friends, Rooms and the 404 — all under the
   * 44 px floor this app has set itself. Here the box really does grow rather than
   * being faked with a pseudo-element: a button is a solid shape a person aims at,
   * and an invisible margin around a visibly-small target is the thing that makes
   * a phone feel imprecise. `lg` is already over the line and is left alone.
   */
  @media (pointer: coarse) {
    .btn {
      height: var(--hit);
    }
  }
  .block {
    width: 100%;
  }

  .primary {
    background: var(--accent);
    color: var(--accent-ink);
  }
  .primary:hover:not(:disabled) {
    background: var(--accent-hot);
  }

  .secondary {
    background: var(--surface-2);
    border-color: var(--line);
    color: var(--text);
  }
  .secondary:hover:not(:disabled) {
    background: var(--surface-3);
  }

  /**
   * THE WHITE PLAY BUTTON — Netflix's, in Jovii's ivory.
   *
   * A SEPARATE VARIANT AND NOT A RESTYLED `primary`, deliberately. `primary` is
   * the brand action and it is crimson everywhere in this app — the Gate's
   * submit, "Take it" in the handle editor, "Continue watching" in a sheet.
   * Repainting it ivory to satisfy one screen would repaint all of those, and
   * the crimson primary IS the brand's voice at every other call site.
   *
   * The colours are literal rather than tokens because they must NOT follow the
   * theme: the phone hero's Play is ivory-on-wine-black in Dark and in Light
   * alike, exactly as Netflix's is white in both of its themes. `--text` happens
   * to be #f7f1e8 today in both themes, so reading the token would look right and
   * would silently become wrong the day a third theme exists. `currentColor`
   * carries the ink into the play glyph, which is what makes the triangle black.
   */
  .hero {
    background: #f7f1e8;
    border-color: #f7f1e8;
    color: #1a0508;
  }
  .hero:hover:not(:disabled) {
    background: #fffaf3;
    border-color: #fffaf3;
  }

  .ghost {
    background: none;
    color: var(--text-dim);
  }
  .ghost:hover:not(:disabled) {
    background: var(--surface-2);
    color: var(--text);
  }

  .btn:active:not(:disabled) {
    transform: translateY(0.5px);
  }
  .btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
</style>
